import React, { useState, useEffect, useCallback } from "react";
import api from "../../api";
import styles from "./QuestionLibrary.module.css";

const TYPE_CONFIG = {
  p: { label: "Programming", bg: "#e3f0f9", fg: "#0f6ca8" },
  programming: { label: "Programming", bg: "#e3f0f9", fg: "#0f6ca8" },
  s: { label: "Subjective", bg: "#f0ecfa", fg: "#6b4fb5" },
  subjective: { label: "Subjective", bg: "#f0ecfa", fg: "#6b4fb5" },
  o: { label: "MCQ", bg: "#e6f6ec", fg: "#1f8a4c" },
  mcq: { label: "MCQ", bg: "#e6f6ec", fg: "#1f8a4c" },
};

const DIFFICULTY_CONFIG = {
  E: { label: "Easy", bg: "#e6f6ec", fg: "#1f8a4c" },
  M: { label: "Medium", bg: "#fff8ec", fg: "#b07d1a" },
  H: { label: "Hard", bg: "#fef2f2", fg: "#c93c3c" },
};

const normalizeType = (type) => {
  const map = { programming: "p", subjective: "s", mcq: "o" };
  return map[type] || type;
};

const ManageQuestionsModal = ({ isOpen, onClose, topicId, topicName, isCourseTemplate = false }) => {
  const [assignedQuestions, setAssignedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const fetchAssigned = useCallback(async () => {
    if (!topicId) return;
    setLoading(true);
    try {
      let questions = [];

      if (isCourseTemplate) {
        // Cohort mode - use course-template content API
        const response = await api.get(`api/course-template/${topicId}/content/`);
        const assessments = response.data.assessments || [];
        questions = assessments.map((q) => ({
          ...q,
          type: normalizeType(q.type),
          question_ref: q.meta || "",
        }));
      } else {
        // 1:1 mode - use course questions API
        const response = await api.get(`api/course/${topicId}/questions/assign/`);
        questions = response.data.questions || [];
      }

      setAssignedQuestions(questions);
    } catch (err) {
      console.error("Failed to fetch assigned:", err);
    } finally {
      setLoading(false);
    }
  }, [topicId, isCourseTemplate]);

  useEffect(() => {
    if (isOpen) {
      fetchAssigned();
      setSearchQuery("");
      setTypeFilter("");
    }
  }, [isOpen, fetchAssigned]);

  const filteredQuestions = React.useMemo(() => {
    let filtered = assignedQuestions;

    if (typeFilter) {
      filtered = filtered.filter((q) => normalizeType(q.type) === typeFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (q) =>
          q.title?.toLowerCase().includes(query) ||
          q.question_ref?.toLowerCase().includes(query) ||
          q.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [assignedQuestions, typeFilter, searchQuery]);

  const calculateTotals = () => {
    return {
      count: assignedQuestions.length,
      maxScore: assignedQuestions.reduce((sum, q) => sum + (q.max_score || 0), 0),
      programming: assignedQuestions.filter((q) => normalizeType(q.type) === "p").length,
      subjective: assignedQuestions.filter((q) => normalizeType(q.type) === "s").length,
      mcq: assignedQuestions.filter((q) => normalizeType(q.type) === "o").length,
    };
  };

  const totals = calculateTotals();

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={() => onClose(false)}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "600px", height: "auto", maxHeight: "85vh" }}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderInfo}>
            <h2 className={styles.modalTitle}>
              Questions — {topicName || "Module"}
            </h2>
            <div className={styles.modalSubtitle}>
              {totals.count} questions · Max score {totals.maxScore}
            </div>
          </div>
          <button
            onClick={() => onClose(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#66808f",
              padding: "4px 8px"
            }}
          >
            ✕
          </button>
        </div>

        {/* Search & Filters */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #e4eaf0", background: "#fbfdff" }}>
          <input
            type="text"
            className={styles.panelSearch}
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ marginBottom: "10px", width: "100%" }}
          />
          <div className={styles.panelFilters}>
            {[
              { key: "", label: "All" },
              { key: "p", label: "Programming" },
              { key: "s", label: "Subjective" },
              { key: "o", label: "MCQ" },
            ].map((f) => (
              <button
                key={f.key}
                className={`${styles.panelFilterChip} ${
                  typeFilter === f.key ? styles.active : ""
                }`}
                onClick={() => setTypeFilter(f.key === typeFilter ? "" : f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ display: "block", overflow: "hidden" }}>
          <div style={{ display: "flex", flexDirection: "column", background: "#fbfdff" }}>
            <div style={{ minHeight: "300px", maxHeight: "400px", overflowY: "auto" }}>
              {loading ? (
                <div className={styles.loading}>Loading...</div>
              ) : filteredQuestions.length === 0 ? (
                <div className={styles.empty}>
                  {assignedQuestions.length === 0
                    ? "No questions assigned to this module yet."
                    : "No questions match your search."}
                </div>
              ) : (
                filteredQuestions.map((q, idx) => {
                  const typeConfig = TYPE_CONFIG[q.type] || TYPE_CONFIG.p;
                  const diffConfig = DIFFICULTY_CONFIG[q.difficulty] || DIFFICULTY_CONFIG.M;

                  return (
                    <div key={`${q.type}_${q.id}`} className={styles.assignedRow}>
                      <span className={styles.assignedNumber}>{idx + 1}</span>
                      <div className={styles.panelRowInfo}>
                        <div className={styles.panelRowTitle}>{q.title}</div>
                        <div className={styles.panelRowMeta}>
                          {q.question_ref || q.meta || ""} · {typeConfig.label} · {q.max_score || 0} pts
                        </div>
                      </div>
                      <span
                        className={styles.panelRowDiff}
                        style={{
                          background: diffConfig.bg,
                          color: diffConfig.fg,
                        }}
                      >
                        {diffConfig.label}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{
              padding: "12px 20px",
              borderTop: "1px solid #eef2f6",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "13px",
              color: "#66808f"
            }}>
              <span>Programming / Subjective / MCQ</span>
              <span style={{ fontWeight: 600, color: "#16232e" }}>
                {totals.programming} / {totals.subjective} / {totals.mcq}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageQuestionsModal;
